import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminMerchantsGetAdminClientsClientIdGuidMerchantsQueryKey,
  getAdminMerchantsGetAdminMerchantsQueryKey,
  useAdminMerchantsPostAdminClientsClientIdGuidMerchants,
} from '@/lib/api/admin-merchants/admin-merchants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  merchantId: z.string().min(1, 'Merchant ID is required.').max(64),
  name: z.string().min(1, 'Name is required.').max(200),
  balanceAccountId: z.string().max(64).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

interface Props {
  clientId: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Admin-side "Add merchant" dialog, launched from the Client detail page.
 * Thin wrapper around `POST /admin/clients/{clientId}/merchants`.
 */
export function AdminCreateMerchantDialog({ clientId, onOpenChange }: Props) {
  const open = clientId !== null;
  const queryClient = useQueryClient();

  const mutation = useAdminMerchantsPostAdminClientsClientIdGuidMerchants({
    mutation: {
      onSuccess: async () => {
        if (clientId) {
          await queryClient.invalidateQueries({
            queryKey: getAdminMerchantsGetAdminClientsClientIdGuidMerchantsQueryKey(clientId),
          });
        }
        await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() });
        toast.success('Merchant created.');
        reset();
        onOpenChange(false);
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { merchantId: '', name: '', balanceAccountId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!clientId) return;
    try {
      await mutation.mutateAsync({
        clientId,
        data: {
          merchantId: values.merchantId,
          name: values.name,
          balanceAccountId: values.balanceAccountId || null,
        },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'A merchant with that id already exists under this client.' : 'Could not create merchant.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add merchant</DialogTitle>
          <DialogDescription>
            Adds an Adyen merchant account under this client. Stores live beneath each merchant.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="create-merchant-id">Merchant ID (Adyen merchant-account code)</Label>
            <Input id="create-merchant-id" placeholder="DealerWorksPOS" autoComplete="off" {...register('merchantId')} />
            {errors.merchantId && <p className="text-xs text-destructive">{errors.merchantId.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-merchant-name">Name</Label>
            <Input id="create-merchant-name" placeholder="Dealer Sales" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-merchant-balance">Balance Account ID (optional)</Label>
            <Input id="create-merchant-balance" placeholder="BA32..." autoComplete="off" {...register('balanceAccountId')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create merchant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
